import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import type { ReactElement } from 'react'
import { ModalBackdrop } from './ModalBackdrop'

interface ClearAllConfirmProps {
  step: 1 | 2
  onNext: () => void
  onCancel: () => void
  onConfirm: () => Promise<void>
  clearing: boolean
}

export function ClearAllConfirm({
  step,
  onNext,
  onCancel,
  onConfirm,
  clearing,
}: ClearAllConfirmProps): ReactElement {
  const isFinalStep = step === 2

  return (
    <>
      <ModalBackdrop ariaLabel="Cancel" onClose={onCancel} />
      <div className="fixed inset-0 z-50 m-auto h-fit w-[calc(100%-2rem)] max-w-md rounded-2xl bg-white px-4 pt-6 pb-8 shadow-2xl sm:shadow-md">
        <div className="mb-4 flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
        </div>

        <h2 className="mb-1 text-center font-semibold text-gray-900 text-lg">
          {isFinalStep ? 'Are you absolutely sure?' : 'Clear All Data?'}
        </h2>
        <p className="mb-6 text-center text-gray-500 text-sm">
          {isFinalStep ? (
            <>
              This is your <strong>final confirmation</strong>. All habit data
              will be gone forever.
            </>
          ) : (
            <>
              This will permanently delete <strong>all habits and logs</strong>.
              This action cannot be undone.
            </>
          )}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl border border-gray-300 py-2.5 font-medium text-gray-700 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={isFinalStep ? onConfirm : onNext}
            disabled={isFinalStep && clearing}
            className={`flex-1 rounded-xl py-2.5 font-medium text-sm text-white disabled:opacity-60 ${
              isFinalStep
                ? 'bg-red-700 hover:bg-red-800'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isFinalStep
              ? clearing
                ? 'Deleting…'
                : 'Delete permanently'
              : 'Yes, delete everything'}
          </button>
        </div>
      </div>
    </>
  )
}
